import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  MdCheck,
  MdClose,
  MdCloudUpload,
  MdKeyboardArrowDown,
  MdRestartAlt,
  MdInfoOutline,
  MdCheckroom,
  MdOutlineImage,
} from "react-icons/md";
import styles from "./ProductUploadModal.module.css";
import { useProductUploadModal } from "../../customHook/ProductUploadModal/useProductUploadModal";

const ProductUploadModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  getFilterCategories,
  getCollectionCategories,
  defaultGender = "",
  defaultMode = "",
  productToEdit = null,
  heading,
}) => {
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    previewUrls,
    optionsLoading,
    optionsError,
    filterOptions,
    collectionOptions,
    formRef,
    handleInputChange,
    handleInputBlur,
    handleImageChange,
    handleRemoveImage,
    handleSubmit,
    handleClose,
    handleReset,
    handleCancel,
    handleBackdropClick,
    handleToggleCategory,
    isDetailsFilled,
    isConfigFilled,
    isImagesFilled,
    isEditMode,
    hasChanges,
    isFormValid,
    isFormDirty,
  } = useProductUploadModal({
    isOpen,
    onClose,
    onSubmit,
    getFilterCategories,
    getCollectionCategories,
    defaultGender,
    defaultMode,
    productToEdit,
  });

  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isCollectionDropdownOpen, setIsCollectionDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);
  const collectionDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setIsFilterDropdownOpen(false);
      }
      if (
        collectionDropdownRef.current &&
        !collectionDropdownRef.current.contains(event.target)
      ) {
        setIsCollectionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || optionsLoading) {
      setIsFilterDropdownOpen(false);
      setIsCollectionDropdownOpen(false);
    }
  }, [isOpen, optionsLoading]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (isFilterDropdownOpen) {
          setIsFilterDropdownOpen(false);
          return;
        }
        if (isCollectionDropdownOpen) {
          setIsCollectionDropdownOpen(false);
          return;
        }
        if (!isLoading) {
          handleCancel();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isLoading, isFilterDropdownOpen, isCollectionDropdownOpen, handleCancel]);

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  const modalHeading = heading || (isEditMode ? "Edit Product" : "Add New Product");

  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={handleBackdropClick}
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>{modalHeading}</h2>
          </div>
          <button className={styles.closeButton} onClick={handleCancel} disabled={isLoading}>
            <MdClose />
          </button>
        </div>

        <div className={styles.progressContainer}>
          <div className={`${styles.progressStep} ${isDetailsFilled ? styles.stepDone : styles.stepActive}`}>
            <span className={styles.stepDot}>{isDetailsFilled ? <MdCheck size={11} /> : "1"}</span>
            <span className={styles.stepText}>Details</span>
          </div>
          <div className={styles.progressLine} />
          <div className={`${styles.progressStep} ${isConfigFilled ? styles.stepDone : (isDetailsFilled ? styles.stepActive : styles.stepInactive)}`}>
            <span className={styles.stepDot}>{isConfigFilled ? <MdCheck size={11} /> : "2"}</span>
            <span className={styles.stepText}>Configuration</span>
          </div>
          <div className={styles.progressLine} />
          <div className={`${styles.progressStep} ${isImagesFilled ? styles.stepDone : (isConfigFilled ? styles.stepActive : styles.stepInactive)}`}>
            <span className={styles.stepDot}>{isImagesFilled ? <MdCheck size={11} /> : "3"}</span>
            <span className={styles.stepText}>Images</span>
          </div>
        </div>

        <form ref={formRef} className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.formFields}>
            {/* 01 BASIC PRODUCT DETAILS */}
            <div className={styles.sectionHeader}>
            
              <MdInfoOutline className={styles.sectionHeaderIcon} />
              <span className={styles.sectionTitle}>Basic Details</span>
            </div>

            {/* Title - Full Row Space */}
            <div className={styles.formGroup}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                <label className={styles.label} htmlFor="title" style={{ marginBottom: 0 }}>
                  Title <span className={styles.required}>*</span>
                </label>
                <span className={styles.charCount}>{formData.title.length}/100</span>
              </div>
              <input
                id="title"
                name="title"
                type="text"
                maxLength="100"
                className={`${styles.input} ${errors.title ? styles.inputError : ""}`}
                placeholder="Enter title (3-100 chars, e.g. Silk Shirt - Classic (Blue))"
                value={formData.title}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                disabled={isLoading}
              />
              {errors.title && <span className={styles.errorMessage}>{errors.title}</span>}
            </div>

            {/* New Row: SKU ID and Price */}
            <div className={styles.skuPriceRow}>
              <div className={styles.formGroup}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                  <label className={styles.label} htmlFor="sku" style={{ marginBottom: 0 }}>
                    SKU ID <span className={styles.required}>*</span>
                  </label>
                  <span className={styles.charCount}>
                    {isEditMode ? "Locked" : `${formData.sku.length}/20`}
                  </span>
                </div>
                <input
                  id="sku"
                  name="sku"
                  type="text"
                  maxLength="20"
                  className={`${styles.input} ${errors.sku ? styles.inputError : ""} ${isEditMode ? styles.inputReadOnly : ""}`}
                  placeholder="e.g. SKU-1001"
                  value={formData.sku}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  disabled={isLoading || isEditMode}
                  readOnly={isEditMode}
                  title={isEditMode ? "Product SKU cannot be modified" : ""}
                />
                {errors.sku && <span className={styles.errorMessage}>{errors.sku}</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="price">
                  Price <span className={styles.required}>*</span>
                </label>
                <input
                  id="price"
                  name="price"
                  type="text"
                  className={`${styles.input} ${errors.price ? styles.inputError : ""}`}
                  placeholder="Enter price"
                  value={formData.price}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                {errors.price && <span className={styles.errorMessage}>{errors.price}</span>}
              </div>
            </div>

            <div className={styles.formGroup}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                <label className={styles.label} htmlFor="description" style={{ marginBottom: 0 }}>
                  Description
                </label>
                <span className={styles.optional}>Optional</span>
              </div>
              <textarea
                id="description"
                name="description"
                maxLength="2000"
                className={`${styles.textarea} ${errors.description ? styles.inputError : ""}`}
                placeholder="Add a short description..."
                value={formData.description}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.description && <span className={styles.errorMessage}>{errors.description}</span>}
              <span className={styles.charCount}>{formData.description.length}/2000</span>
            </div>

            {/* 02 GARMENT CONFIGURATION */}
            <div className={styles.sectionHeader}>
           
              <MdCheckroom className={styles.sectionHeaderIcon} />
              <span className={styles.sectionTitle}>Garment Configuration</span>
            </div>

            <div className={styles.rowGroup}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="gender">
                  Gender <span className={styles.required}>*</span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  className={`${styles.select} ${errors.gender ? styles.inputError : ""}`}
                  value={formData.gender}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  disabled={isLoading}
                >
                  <option value="">Select Gender</option>
                  <option value="women">Women</option>
                  <option value="men">Men</option>
                </select>
                {errors.gender && <span className={styles.errorMessage}>{errors.gender}</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="mode">
                  Mode <span className={styles.required}>*</span>
                </label>
                <select
                  id="mode"
                  name="mode"
                  className={`${styles.select} ${errors.mode ? styles.inputError : ""}`}
                  value={formData.mode}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  disabled={isLoading}
                >
                  <option value="">Select Model</option>
                  <option value="model_to_model">Model to Model</option>
                  <option value="mannequin_to_model">Mannequin to Model</option>
                </select>
                {errors.mode && <span className={styles.errorMessage}>{errors.mode}</span>}
              </div>
            </div>

            {optionsError && (
              <p className={styles.errorMessage}>{optionsError}</p>
            )}

            <div className="flex flex-col gap-1.5 relative" ref={filterDropdownRef}>
              <div className="flex items-center justify-between">
                <label className={styles.label}>
                  Filter Categories <span className={styles.required}>*</span>
                </label>
                {optionsLoading && (
                  <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-ping" />
                    Loading...
                  </span>
                )}
              </div>
              <div className="relative w-full">
                {/* Multi-Select Trigger Box */}
                <button
                  id="filterCategories"
                  type="button"
                  className={`w-full min-h-[36px] px-2.5 py-1 bg-white text-slate-800 border rounded-md flex items-center justify-between cursor-pointer text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                    errors.filterCategories ? "border-red-500" : "border-slate-300 hover:border-slate-400"
                  } ${optionsLoading ? "opacity-75 cursor-wait" : ""}`}
                  onClick={() => !optionsLoading && setIsFilterDropdownOpen((prev) => !prev)}
                  disabled={isLoading || optionsLoading || filterOptions.length === 0}
                >
                  <div className="flex-1 min-w-0 flex items-center overflow-hidden">
                    {optionsLoading ? (
                      <span className="text-slate-400 text-xs font-medium truncate flex items-center gap-1.5">
                        <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin inline-block shrink-0" />
                        Loading categories...
                      </span>
                    ) : formData.filterCategories.length === 0 ? (
                      <span className="text-slate-400 text-xs font-medium truncate">
                        {filterOptions.length === 0
                          ? "No Filter categories for this selection"
                          : "Select Filter categories..."}
                      </span>
                    ) : (
                          <div className={`flex flex-wrap gap-1 ${styles.selectedFilterChips}`}>
                            {formData.filterCategories.map((cat) => (
                              <span
                                key={cat}
                                className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 border border-slate-300 rounded px-1.5 py-0.5 text-[11px] font-semibold leading-tight"
                              >
                                {cat}
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="inline-flex items-center justify-center text-slate-500 hover:text-slate-900 rounded-full p-0.5 cursor-pointer hover:bg-slate-200 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData((prev) => ({
                                      ...prev,
                                      filterCategories: prev.filterCategories.filter((c) => c !== cat),
                                    }));
                                  }}
                                >
                                  <MdClose size={10} />
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <MdKeyboardArrowDown
                        size={16}
                        className={`text-slate-500 transition-transform duration-200 ml-1.5 shrink-0 ${
                          isFilterDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Multi-Select Dropdown Menu */}
                    {isFilterDropdownOpen && filterOptions.length > 0 && (
                      <div className={`absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl py-1 text-xs divide-y divide-slate-100 ${styles.dropdownList}`}>
                        {filterOptions.map((catItem) => {
                          const label = catItem.label || catItem;
                          const categoryId = catItem.iCategoryId;
                          const isActive = catItem.isActive !== false;
                          const isSelected = formData.filterCategories.includes(label);

                          return (
                            <div
                              key={categoryId || label}
                              className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer transition-colors ${
                                isSelected ? "bg-slate-50 font-semibold text-primary" : "text-slate-700 hover:bg-slate-50/80"
                              } ${!isActive ? "opacity-75" : ""}`}
                              onClick={() => {
                                setFormData((prev) => {
                                  const exists = prev.filterCategories.includes(label);
                                  const nextList = exists
                                    ? prev.filterCategories.filter((item) => item !== label)
                                    : [...prev.filterCategories, label];
                                  return {
                                    ...prev,
                                    filterCategories: nextList,
                                  };
                                });
                                if (errors.filterCategories) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    filterCategories: "",
                                  }));
                                }
                              }}
                            >
                              {/* Left side: Checkbox + Label */}
                              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                <div
                                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${
                                    isSelected
                                      ? "bg-primary border-primary text-on-primary shadow-sm"
                                      : "border-slate-300 bg-white"
                                  }`}
                                >
                                  {isSelected && <MdCheck size={12} className="font-bold" />}
                                </div>
                                <span className="truncate text-slate-800 text-xs">{label}</span>
                              </div>

                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.filterCategories && (
                    <span className={styles.errorMessage}>{errors.filterCategories}</span>
                  )}
                </div>

                {/* Collection Category Custom Dropdown */}
                <div className="flex flex-col gap-1.5 relative" ref={collectionDropdownRef}>
                  <div className="flex items-center justify-between">
                    <label className={styles.label} htmlFor="collectionCategory">
                      Collection Category <span className={styles.required}>*</span>
                    </label>
                    {optionsLoading && (
                      <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-ping" />
                        Loading...
                      </span>
                    )}
                  </div>
                  <div className="relative w-full">
                    {/* Single-Select Trigger Box */}
                    <button
                      id="collectionCategory"
                      type="button"
                      className={`w-full min-h-[36px] px-2.5 py-1.5 bg-white text-slate-800 border rounded-md flex items-center justify-between cursor-pointer text-left transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        errors.collectionCategory ? "border-red-500" : "border-slate-300 hover:border-slate-400"
                      } ${optionsLoading ? "opacity-75 cursor-wait" : ""}`}
                      onClick={() => !optionsLoading && setIsCollectionDropdownOpen((prev) => !prev)}
                      disabled={isLoading || optionsLoading || collectionOptions.length === 0}
                    >
                      <span className="text-xs truncate">
                        {optionsLoading ? (
                          <span className="text-slate-400 font-medium flex items-center gap-1.5">
                            <span className="w-3 h-3 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin inline-block shrink-0" />
                            Loading collections...
                          </span>
                        ) : formData.collectionCategory ? (
                          <span className="font-medium text-slate-900">{formData.collectionCategory}</span>
                        ) : (
                          <span className="text-slate-400 font-medium">
                            {collectionOptions.length === 0
                              ? "No collection categories for this selection"
                              : "Select Collection Category"}
                          </span>
                        )}
                      </span>
                      <MdKeyboardArrowDown
                        size={16}
                        className={`text-slate-500 transition-transform duration-200 ml-1.5 shrink-0 ${
                          isCollectionDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Single-Select Dropdown Menu */}
                    {isCollectionDropdownOpen && collectionOptions.length > 0 && (
                      <div className={`absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-xl py-1 text-xs divide-y divide-slate-100 ${styles.dropdownList}`}>
                        {collectionOptions.map((catItem) => {
                          const label = catItem.label || catItem;
                          const categoryId = catItem.iCategoryId;
                          const isActive = catItem.isActive !== false;
                          const isSelected = formData.collectionCategory === label;

                          return (
                            <div
                              key={categoryId || label}
                              className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer transition-colors ${
                                isSelected ? "bg-slate-50 font-semibold text-primary" : "text-slate-700 hover:bg-slate-50/80"
                              } ${!isActive ? "opacity-75" : ""}`}
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  collectionCategory: label,
                                }));
                                setIsCollectionDropdownOpen(false);
                                if (errors.collectionCategory) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    collectionCategory: "",
                                  }));
                                }
                              }}
                            >
                              {/* Left side: Label + Selection Checkmark */}
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {isSelected && <MdCheck size={14} className="text-primary font-bold shrink-0" />}
                                <span className="truncate text-slate-800 text-xs">{label}</span>
                              </div>

                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {errors.collectionCategory && (
                    <span className={styles.errorMessage}>{errors.collectionCategory}</span>
                  )}
                </div>

            {/* 03 PRODUCT IMAGES */}
            <div className="flex flex-col gap-1 mb-2 mt-5">
              <div className={`${styles.sectionHeader} ${styles.sectionHeaderImages}`}>
                <MdOutlineImage className={styles.sectionHeaderIcon} />
                <span className={styles.sectionTitle}>Product Views</span>
              </div>
              <ul className="m-0 pl-4 flex flex-col gap-0.5 list-disc text-[11px] text-slate-500 leading-normal">
                <li>
                  Allowed formats: <strong className="text-slate-700">JPG, JPEG, PNG</strong> (max 10MB).
                </li>
                <li>
                  File names must contain <code className="bg-slate-100 px-1 py-0.5 rounded text-primary text-[10.5px] font-semibold">front</code> for front view, and <code className="bg-slate-100 px-1 py-0.5 rounded text-primary text-[10.5px] font-semibold">back</code> for back view.
                </li>
              </ul>
            </div>

            <div className={styles.imageUploadGrid}>
              {/* Front Image Upload slot */}
              <div className={styles.uploadSlotWrapper} data-field="frontImage">
                <div id="frontImageCard" className={`${styles.uploadCard} ${errors.frontImage ? styles.uploadCardError : ""}`}>
                  <span className={styles.uploadCardTitle}>Front View <span className={styles.required}>*</span></span>
                  <span className={styles.uploadCardSubtitle}>Filename must contain "front"</span>
                  <input
                    id="frontImage"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleImageChange(e, "front")}
                    style={{ display: "none" }}
                    disabled={isLoading}
                  />
                  {(formData.frontImage || previewUrls.front) ? (
                    <div className={styles.previewWrapper}>
                      <img
                        src={previewUrls.front}
                        alt="Front preview"
                        className={styles.previewImg}
                        onClick={() => {
                          if (!isLoading) {
                            document.getElementById("frontImage")?.click();
                          }
                        }}
                        title="Click to choose a new file"
                        style={{ cursor: "pointer" }}
                      />
                      <button
                        type="button"
                        className={styles.removePreviewBtn}
                        onClick={() => handleRemoveImage("front")}
                        disabled={isLoading}
                        title="Remove image"
                      >
                        <MdClose />
                      </button>
                      <div className={styles.fileDetail}>
                        <span className={styles.fileName}>
                          {formData.frontImage ? `Uploaded: ${formData.frontImage.name}` : "Current: Front View"}
                        </span>
                        {formData.frontImage && (
                          <span className={styles.fileSize}>
                            {(formData.frontImage.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <label htmlFor="frontImage" className={styles.dropZone}>
                      <MdCloudUpload className={styles.dropZoneIcon} />
                      <span className={styles.dropZoneText}>Upload image</span>
                    </label>
                  )}
                </div>
                {errors.frontImage && <span className={styles.errorMessage}>{errors.frontImage}</span>}
              </div>

              {/* Back Image Upload slot */}
              <div className={styles.uploadSlotWrapper} data-field="backImage">
                <div id="backImageCard" className={`${styles.uploadCard} ${errors.backImage ? styles.uploadCardError : ""}`}>
                  <span className={styles.uploadCardTitle}>Back View <span className={styles.required}>*</span></span>
                  <span className={styles.uploadCardSubtitle}>Filename must contain "back"</span>
                  <input
                    id="backImage"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => handleImageChange(e, "back")}
                    style={{ display: "none" }}
                    disabled={isLoading}
                  />
                  {(formData.backImage || previewUrls.back) ? (
                    <div className={styles.previewWrapper}>
                      <img
                        src={previewUrls.back}
                        alt="Back preview"
                        className={styles.previewImg}
                        onClick={() => {
                          if (!isLoading) {
                            document.getElementById("backImage")?.click();
                          }
                        }}
                        title="Click to choose a new file"
                        style={{ cursor: "pointer" }}
                      />
                      <button
                        type="button"
                        className={styles.removePreviewBtn}
                        onClick={() => handleRemoveImage("back")}
                        disabled={isLoading}
                        title="Remove image"
                      >
                        <MdClose />
                      </button>
                      <div className={styles.fileDetail}>
                        <span className={styles.fileName}>
                          {formData.backImage ? `Uploaded: ${formData.backImage.name}` : "Current: Back View"}
                        </span>
                        {formData.backImage && (
                          <span className={styles.fileSize}>
                            {(formData.backImage.size / (1024 * 1024)).toFixed(2)} MB
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <label htmlFor="backImage" className={styles.dropZone}>
                      <MdCloudUpload className={styles.dropZoneIcon} />
                      <span className={styles.dropZoneText}>Upload image</span>
                    </label>
                  )}
                </div>
                {errors.backImage && <span className={styles.errorMessage}>{errors.backImage}</span>}
              </div>
            </div>
          </div>

          <div className={styles.formActions}>
            {isFormDirty && (
              <button
                type="button"
                className={styles.resetButton}
                onClick={handleReset}
                disabled={isLoading}
                title="Reset form"
              >
                <MdRestartAlt size={14} />
                <span>Reset</span>
              </button>
            )}
            <button type="button" className={styles.cancelButton} onClick={handleCancel} disabled={isLoading}>
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading || optionsLoading || !isFormValid || (isEditMode && !hasChanges)}
            >
              {isLoading
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ProductUploadModal;
